"use client";

import React from "react";
import { EnhancedGenericCreateForm } from "@/components/shared/EnhancedGenericCreateForm";
import { getErrorMessage } from "@/components/shared/EnhancedGenericCreateForm/types";
import { TICKET_CONFIG } from "@/features/tickets/components/TicketConfig";
import { useCreateTicket } from "@/lib/hooks/use-api";
import { useFormFeedback } from "@/lib/hooks/forms/use-feedback";

// Static (derived from the module-level TICKET_CONFIG) — hoisted so the memo'd
// EnhancedGenericCreateForm gets a stable `tagSets` reference across renders.
const TICKET_TAG_SETS = {
  TICKET: TICKET_CONFIG.fields.tags?.tagsConfig?.commonTags || [],
};

interface CreateTicketFormSimplifiedProps {
  onSuccess?: (ticketId?: string) => void;
  /** When true, form flows with page scroll instead of using an inner scroll container */
  fullPage?: boolean;
}

const CreateTicketFormSimplified: React.FC<CreateTicketFormSimplifiedProps> = ({
  onSuccess,
  fullPage = false,
}) => {
  const { mutateAsync: createTicket, isPending } = useCreateTicket();
  const feedback = useFormFeedback({
    successMessage: "Ticket created successfully! 🎉",
  });

  const handleSubmit = async (data: any) => {
    try {
      const submitData = { ...data };
      const result = await createTicket(submitData);
      feedback.showSuccess("Ticket created successfully! 🎉");
      onSuccess?.((result as { id?: string })?.id);
    } catch (error) {
      feedback.showError(error);
      throw error;
    }
  };

  return (
    <div className="space-y-4">
      <EnhancedGenericCreateForm
        entityName="ticket"
        config={TICKET_CONFIG}
        onSubmit={handleSubmit}
        isLoading={isPending}
        fullPage={fullPage}
        expandInlineVariant={fullPage ? "floating" : "replace"}
        tagSets={TICKET_TAG_SETS}
      />

      {/* Form Feedback Display */}
      <div className="space-y-2">
        {feedback.hasError && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-md dark:bg-red-950/40 dark:border-red-800">
            <p className="text-sm text-red-800 dark:text-red-200">
              {getErrorMessage(feedback.error)}
            </p>
          </div>
        )}
        {feedback.hasSuccess && (
          <div className="p-3 bg-green-50 border border-green-200 rounded-md dark:bg-green-950/40 dark:border-green-800">
            <p className="text-sm text-green-800 dark:text-green-200">{feedback.successMessage}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CreateTicketFormSimplified;
